import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useState, type CSSProperties } from "react";
import {
  listFestivals,
  listRiders,
  type FestivalListItem,
  type RiderListItem,
} from "@/api/client";
import { useActiveOrgId } from "@/hooks/useActiveOrg";
import { useAuthStore } from "@/stores/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Stagger } from "@/components/Animate";
import { DashboardHeroUpload } from "@/components/dashboard/DashboardHeroUpload";
import { DashboardNext } from "@/components/dashboard/DashboardNext";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { ExploreMoreCard } from "@/components/dashboard/ExploreMoreCard";
import { StatusBadge, type RiderStatus } from "@/components/ds";
import { Calendar, FileText, Upload, AlertTriangle, ArrowRight, Music, type LucideIcon } from "lucide-react";

const FESTIVAL_COLORS: Record<string, string> = {
  rose: "#f43f5e", orange: "#f97316", amber: "#f59e0b", emerald: "#10b981",
  teal: "#14b8a6", cyan: "#06b6d4", blue: "#3b82f6", indigo: "#6366f1",
  violet: "#8b5cf6", fuchsia: "#d946ef", pink: "#ec4899",
};

function getColorHex(color: string | null): string {
  if (!color) return "var(--tr-stage-main)";
  return FESTIVAL_COLORS[color] ?? "#71717a";
}

const RIDER_STATUS_MAP: Record<string, { s: RiderStatus; label: string }> = {
  pending: { s: "pending", label: "Pending" },
  extracting: { s: "processing", label: "Extracting" },
  extracted: { s: "extracted", label: "Review" },
  approved: { s: "approved", label: "Approved" },
  failed: { s: "failed", label: "Failed" },
};

type LayoutVariant = "next" | "classic";
const fixedNewLayout = (): LayoutVariant => "next";

export function DashboardPage() {
  const activeOrgId = useActiveOrgId();
  const organizations = useAuthStore((s) => s.organizations);
  const layout = fixedNewLayout();

  const festivalsQuery = useQuery({
    queryKey: ["festivals", activeOrgId],
    queryFn: () => listFestivals(activeOrgId),
    staleTime: 20_000,
    placeholderData: (previous) => previous,
  });

  const ridersQuery = useQuery({
    queryKey: ["riders", activeOrgId],
    queryFn: () => listRiders(undefined, activeOrgId),
    staleTime: 10_000,
    placeholderData: (previous) => previous,
  });

  // Publish the two root datasets as one snapshot. During focus/manual
  // refetches the previous snapshot remains mounted, so cards and attention
  // rows do not jump through intermediate, internally inconsistent values.
  const [snapshot, setSnapshot] = useState<{
    orgId: string | null;
    festivals: FestivalListItem[];
    riders: RiderListItem[];
  } | null>(null);

  useEffect(() => {
    if (
      festivalsQuery.data === undefined ||
      ridersQuery.data === undefined ||
      festivalsQuery.isFetching ||
      ridersQuery.isFetching
    ) return;

    const timer = window.setTimeout(() => {
      setSnapshot({
        orgId: activeOrgId,
        festivals: festivalsQuery.data,
        riders: ridersQuery.data,
      });
    }, 180);
    return () => window.clearTimeout(timer);
  }, [
    activeOrgId,
    festivalsQuery.data,
    festivalsQuery.isFetching,
    ridersQuery.data,
    ridersQuery.isFetching,
  ]);

  const currentSnapshot = snapshot?.orgId === activeOrgId ? snapshot : null;
  const festivals = currentSnapshot?.festivals;
  const riders = currentSnapshot?.riders;

  const firstFestivalId = festivals?.[0]?.id ?? null;
  const personalOrgId = organizations.find((o) => o.is_personal)?.id ?? null;

  if (!currentSnapshot) {
    return (
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-bold tracking-[-0.025em]" data-ds-display>
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-ink-3">Festival production intelligence at a glance</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="mt-3 h-8 w-16" />
              <Skeleton className="mt-1 h-4 w-24" />
            </div>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] w-full rounded-ds" />
            ))}
          </div>
          <div className="space-y-2 lg:col-span-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-[52px] w-full rounded-ds-sm" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (layout !== "classic") {
    return (
      <DashboardNext
        festivals={festivals ?? []}
        riders={riders ?? []}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.025em]" data-ds-display>
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-ink-3">Festival production intelligence at a glance</p>
        </div>
      </div>
      <DashboardClassic
        festivals={festivals ?? []}
        riders={riders ?? []}
        firstFestivalId={firstFestivalId}
        personalOrgId={personalOrgId}
      />
    </div>
  );
}

function DashboardClassic({
  festivals,
  riders,
  firstFestivalId,
  personalOrgId,
}: {
  festivals: FestivalListItem[];
  riders: RiderListItem[];
  firstFestivalId: string | null;
  personalOrgId: string | null;
}) {
  const festivalCount = festivals.length;
  const riderCount = riders.length;
  const extractingCount = riders.filter((r) => r.status === "extracting").length;
  const reviewCount = riders.filter((r) => r.status === "extracted").length;

  const formatDate = (dateStr: string) =>
    new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const recentRiders = riders.slice(-8).reverse();

  return (
    <>
      <DashboardHeroUpload />

      <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" gap={60} delay={60}>
        <StatCard icon={Calendar} label="Festivals" value={festivalCount} href="/festivals" color="var(--tr-accent)" />
        <StatCard icon={FileText} label="Riders" value={riderCount} href="/riders" color="var(--tr-warn)" />
        <StatCard icon={Upload} label="Extracting" value={extractingCount} href="/riders?status=extracting" color="var(--tr-info)" />
        <StatCard icon={AlertTriangle} label="Needs review" value={reviewCount} href="/riders?status=extracted" color="var(--tr-bad)" />
      </Stagger>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
        <OnboardingChecklist firstFestivalId={firstFestivalId} personalOrgId={personalOrgId} />
        <ExploreMoreCard firstFestivalId={firstFestivalId} />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Recent festivals */}
        <div className="space-y-4 lg:col-span-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
              Recent festivals
            </h3>
            <Link to="/festivals" className="flex items-center gap-1 text-xs text-ink-3 transition-colors hover:text-ink">
              View all <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
            </Link>
          </div>
          {festivals.length > 0 ? (
            <Stagger className="space-y-2.5" gap={50} delay={120}>
              {festivals.slice(0, 4).map((f) => {
                const hex = getColorHex(f.color);
                return (
                  <Link
                    key={f.id}
                    to={`/festivals/${f.id}`}
                    className="card group flex items-center gap-4 p-4 transition-colors hover:border-line-3"
                  >
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-sm text-sm font-bold"
                      style={{ backgroundColor: `${hex}18`, color: hex } as CSSProperties}
                    >
                      {f.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ink">{f.name}</p>
                      <div className="mt-0.5 flex items-center gap-2 font-mono text-[11px] text-ink-3">
                        <span className="tabular-nums">
                          {formatDate(f.start_date)} — {formatDate(f.end_date)}
                        </span>
                        {f.location && (
                          <>
                            <span className="h-3 w-px bg-line-2" />
                            <span>{f.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="rounded-md bg-panel-3 px-2 py-0.5 text-[11px] font-medium tabular-nums text-ink-3">
                      {f.stage_count} stage{f.stage_count !== 1 ? "s" : ""}
                    </span>
                  </Link>
                );
              })}
            </Stagger>
          ) : (
            <div className="flex flex-col items-center rounded-ds border border-dashed border-line-3 px-6 py-14">
              <Calendar className="h-8 w-8 text-ink-4" strokeWidth={1.6} />
              <p className="mt-3 text-sm text-ink-3">No festivals yet</p>
              <Link to="/festivals" className="mt-2 text-sm text-accent hover:underline">
                Create your first festival
              </Link>
            </div>
          )}
        </div>

        {/* Recent riders */}
        <div className="space-y-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
              Recent riders
            </h3>
            <Link to="/riders" className="flex items-center gap-1 text-xs text-ink-3 transition-colors hover:text-ink">
              View all <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
            </Link>
          </div>
          {recentRiders.length > 0 ? (
            <Stagger className="space-y-1.5" gap={40} delay={180}>
              {recentRiders.map((r) => {
                const m = RIDER_STATUS_MAP[r.status] ?? { s: "pending" as RiderStatus, label: r.status };
                return (
                  <Link
                    key={r.id}
                    to={`/riders/${r.id}/review`}
                    className="card group flex items-center gap-3 px-4 py-2.5 transition-colors hover:border-line-3"
                  >
                    <Music className="h-3.5 w-3.5 shrink-0 text-ink-4" strokeWidth={1.6} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink-2 transition-colors group-hover:text-ink">
                        {r.artist_name}
                      </p>
                    </div>
                    <span className="text-[10px] text-ink-4">{r.rider_type}</span>
                    <StatusBadge status={m.s} label={m.label} />
                  </Link>
                );
              })}
            </Stagger>
          ) : (
            <div className="flex flex-col items-center rounded-ds border border-dashed border-line-3 px-6 py-14">
              <FileText className="h-8 w-8 text-ink-4" strokeWidth={1.6} />
              <p className="mt-3 text-sm text-ink-3">No riders uploaded</p>
              <Link to="/riders/upload" className="mt-2 text-sm text-accent hover:underline">
                Upload your first rider
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  href,
  color,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  href: string;
  color: string;
}) {
  return (
    <Link
      to={href}
      className="card group relative overflow-hidden p-5 transition-colors hover:border-line-3"
      style={{ borderColor: `color-mix(in oklab, ${color} 25%, var(--tr-border-2))` } as CSSProperties}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(ellipse 120% 100% at 0% 0%, color-mix(in oklab, ${color} 16%, transparent) 0%, transparent 60%)` }}
      />
      <div className="relative">
        <div className="flex items-center justify-between">
          <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.6} />
          <ArrowRight className="h-3.5 w-3.5 text-ink-4 opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.6} />
        </div>
        <p className="mt-3 font-mono text-3xl font-bold tabular-nums text-ink">{value}</p>
        <p className="mt-0.5 text-xs font-medium text-ink-3">{label}</p>
      </div>
    </Link>
  );
}
